/**
 * @file CalculatedCostPanel.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-17
 * @modified 2026-08-17
 * @description What a formulated blend costs in raw materials, next to what the
 *              cost price actually says.
 *
 *              Shared rather than inlined because there are TWO inventory item
 *              modals — InventoryItemModal and the near-verbatim copy inside
 *              SearchInventory — and a panel written twice would drift.
 *
 *              Coverage is always shown with the figure. A cost covering 82% of a
 *              recipe is useful, but it must not read as if it covered all of it:
 *              the missing ingredients make it an UNDERSTATEMENT, so comparing it
 *              to the real cost price without that context would suggest the real
 *              price is too high when it may be correct.
 *
 *              Renders nothing at all when the item has no formulation or nothing
 *              could be priced — an empty panel on every non-blend item would be
 *              noise on the majority of the inventory.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { formatCurrency } from '../utils/currencyFormatter';
import './BlendCalculator.css';

/**
 * How far apart the calculated and recorded costs have to be before it's worth
 * flagging. Raw material prices move constantly and a formulation is a snapshot,
 * so small gaps are normal; 5% is where "about right" becomes "look at this".
 */
const TOLERANCE_PCT = 5;

export default function CalculatedCostPanel({ itemId, currentCost }) {
  const [data, setData] = useState(null);
  const [column, setColumn] = useState(null);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/blends/formulation-cost?itemIds=${itemId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setData(d.costs?.[itemId] || d.costs?.[String(itemId)] || null);
        setColumn(d.column || null);
      })
      // Silent: a user without blend_costs gets a 403, and the right response to
      // that is no panel rather than an error on an unrelated screen.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [itemId]);

  if (!data || !data.priced_pct) return null;

  const current = currentCost === null || currentCost === undefined ? null : Number(currentCost);
  const diff = current === null ? null : data.cost - current;
  const diffPct = current ? (diff / current) * 100 : null;
  const flagged = diffPct !== null && Math.abs(diffPct) > TOLERANCE_PCT;
  const partial = data.coverage_pct < 99.5;

  return (
    <div className="calc-cost-panel">
      <div className="calc-cost-row">
        <div className="calc-cost-figure">
          <span className="calc-cost-label">Calculated from formulation</span>
          <span className="calc-cost-value">{formatCurrency(data.cost)}</span>
        </div>
        <div className="calc-cost-figure">
          <span className="calc-cost-label">Cost price on the item</span>
          <span className="calc-cost-value">
            {current === null ? '—' : formatCurrency(current)}
          </span>
        </div>
        {diff !== null && (
          <div className="calc-cost-figure">
            <span className="calc-cost-label">Difference</span>
            <span className={`calc-cost-value${flagged ? ' calc-cost-flag' : ''}`}>
              {diff > 0 ? '+' : ''}{formatCurrency(diff)}
              <em>{diffPct > 0 ? '+' : ''}{diffPct.toFixed(1)}%</em>
            </span>
          </div>
        )}
      </div>

      <p className={`calc-cost-note${partial ? ' warn' : ''}`}>
        {partial ? (
          <>
            Covers {data.coverage_pct}% of the recipe — {data.missing.length}{' '}
            ingredient{data.missing.length === 1 ? '' : 's'} had no price
            {data.missing.length <= 3
              ? ` (${data.missing.map((m) => m.label).join(', ')})`
              : ''}
            , so the figure is an understatement.
          </>
        ) : (
          <>Covers the whole recipe.</>
        )}
        {' '}Raw material only, no haulage{column ? `, ${column.replace(/_/g, ' ')} prices` : ''}.
        {data.total_pct < 99.5 && (
          <> The recipe sums to {data.total_pct}%, not 100%, which understates it further.</>
        )}
        {data.ambiguous.length > 0 && (
          <> {data.ambiguous.length} ingredient{data.ambiguous.length === 1 ? '' : 's'}{' '}
            map to more than one material, so the price used is one of several.</>
        )}
      </p>
    </div>
  );
}
